// Router created by tools/gen_web_routes.py; owned by hand from here on.
// routeTree.gen.ts is written by TanStack Router's generator (Vite plugin or `tsr generate`).
import { createRouter } from '@tanstack/react-router'
import { routeTree } from './routeTree.gen'
import { durationMs, isReducedMotion, type NavigationMotion, navigationMotion } from './shared/motion/motion'
import { queryClient } from './shared/query/queryClient'
import { RouteError, RouteSkeleton } from './shared/shell/StatePart'

// The content and the pill are named for the view transition only while it runs: a named element is a stacking
// context, which would hold the content's floating bars (the bulk bar) under the tab bar.
let navigation = 0
function navigating(motion: NavigationMotion) {
  const root = document.documentElement
  const mine = ++navigation
  root.dataset.navigation = motion
  setTimeout(() => {
    if (mine === navigation) delete root.dataset.navigation
  }, durationMs(motion, false) + 100)
}

const viewTransitionTypes = globalThis.CSS?.supports?.('selector(:active-view-transition-type(a))') ?? false

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  // Loaders read through the query cache, so the router never caches on its own (TanStack's advice).
  defaultPreloadStaleTime: 0,
  // The state frame (D193): a slow loader shows the page's outline after 300 ms, a failed one its error, both inside the
  // layout around it, so the shell stays.
  defaultPendingMs: 300,
  defaultPendingComponent: RouteSkeleton,
  defaultErrorComponent: RouteError,
  // The route's movement (D159): from 840 px the page body fades (E22U5), below it push, pop or a tab switch, as a view
  // transition, none under reduced motion; motion.css
  // reads `data-navigation`. A browser without view transition types (the router asks for them) does not move.
  defaultViewTransition: viewTransitionTypes && {
    types: ({ fromLocation, toLocation }) => {
      const motion = navigationMotion(fromLocation?.pathname, toLocation.pathname, isReducedMotion())
      if (motion) navigating(motion)
      return motion ? [motion] : false
    },
  },
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
  interface StaticDataRouteOption {
    screenId?: string
    access?: string
    workspace?: string
    /** The screen draws its own glacier (an Organization section, D182): the shell leaves out the context's. */
    ownHeader?: boolean
    /** From 840 px the screen fills the window's height below the top bar, and its parts scroll on their own (E22U8). */
    fill?: boolean
  }
}
